import { z } from "zod";
import { AnnouncementId } from "./announcements.ts";
import { ConversationId, ConversationKind } from "./conversations.ts";

/**
 * Страница одного окна переписки: порция сообщений и шапка окна.
 *
 * ⚠️ **Отдельным файлом от `conversations.ts`, и это правка по итогам итогового
 * ревью 30.08.2026** (порог в 400 строк из раздела 9 CLAUDE.md). Разрез не по
 * размеру, а по читателю: `conversations.ts` описывает РАЗДЕЛ (ключ окна, список
 * окон, счётчик непрочитанного), здесь описано ОДНО открытое окно. Ключ при этом
 * остался там, в одном месте, и приезжает сюда импортом: сборка и разбор строки
 * `person:<uuid>` не размножаются (шапка `conversations.ts`).
 *
 * Оба файла реэкспортируются из `index.ts`, поэтому снаружи разреза не видно:
 * и потолок страницы, и её схема по-прежнему берутся из `@edu/shared`.
 */

/**
 * Сколько сообщений едет в одной странице переписки.
 *
 * ⚠️ Худшая страница это дюжина ОБЪЯВЛЕНИЙ по `ANNOUNCEMENT_BODY_MAX` знаков
 * (ответ вдвое короче, поэтому страница из ответов легче):
 *
 * | что | сырых | сжатых |
 * |---|---|---|
 * | 10 × 2000 знаков | 35,1 КБ | 12,3 КБ |
 * | **12 × 2000 знаков** | **42,2 КБ** | **13,9 КБ** |
 * | 15 × 2000 знаков | 51,5 КБ | 17,4 КБ |
 * | 20 × 1000 знаков (ответы) | 37,5 КБ | 13,4 КБ |
 * | 30 × 140 знаков (обычная переписка) | 13,4 КБ | 5,4 КБ |
 *
 * Отсюда двенадцать, ровно столько же, сколько было у ленты, и по той же
 * причине: пятнадцать худших строк это 17,4 КБ, то есть страница, которая в
 * режиме белого списка не откроется вовсе.
 *
 * ⚠️ **Разница со снятой лентой в том, что здесь ДВЕНАДЦАТЬ это не потеря.** У
 * ленты за тринадцатой строкой не было ничего: курсора не было, и она была
 * недостижима. У переписки курсор есть (`nextCursor`), поэтому потолок означает
 * размер порции, а не границу видимого. Экран догружает более старые страницы,
 * пока человек листает вверх.
 */
export const CONVERSATION_PAGE_LIMIT = 12;

/* ────────────────────── страница переписки ─────────────────────── */

/**
 * Сообщение в окне: объявление либо ответ, одной формой.
 *
 * ⚠️ **`authorUserId` здесь нет намеренно.** В снятой нитке карточки
 * (`AnnouncementReply`) он был и уезжал ученику вместе с чужими ответами, то
 * есть в видимой группе с открытыми ответами ребёнок получал идентификаторы
 * других детей. Повторять это в новой форме незачем: экрану нужно «моё или
 * чужое» и подпись, а не идентификатор, и `mine` отвечает на первое.
 *
 * ⚠️ **Своей квитанции ученику тоже нет, и это снятие 30.08.2026.** Поле
 * `seenAt` возило ему время, когда он сам открыл сообщение, и ни один экран его
 * не показывал: человеку, читающему сообщение, незачем сообщать, что он его
 * читает. Квитанция при этом жива и своей работы не потеряла, её читает
 * преподаватель числами ниже.
 */
export const ConversationMessage = z.object({
  id: z.string().uuid(),
  kind: z.enum(["post", "reply"]),
  /**
   * Объявление, к которому прикреплён ответ. Нужно ровно для удаления
   * (`DELETE /v1/announcements/:announcementId/replies/:replyId`), у объявления
   * не приезжает: там это тот же `id`.
   */
  postId: AnnouncementId.optional(),
  /** Как автора зовут ДЛЯ ЭТОГО ЧИТАТЕЛЯ (`authorNameFor` на сервере). */
  authorName: z.string(),
  mine: z.boolean(),
  /** `null` это «стёрто по сроку» либо «удалено», см. `announcementReplyState`. */
  body: z.string().nullable(),
  createdAt: z.number().int(),
  deletedAt: z.number().int().nullable(),
  /**
   * Только преподавателю и только у объявления: сколько человек его получило и
   * сколько открыло.
   *
   * ⚠️ **Считается по получателям ЭТОГО разговора, а не объявления целиком.**
   * Объявление, отправленное поимённо троим, лежит в трёх личных окнах, и в
   * окне с Ваней «прочитали 2 из 3» отвечало бы не на тот вопрос: там речь про
   * Ваню. В групповом окне разницы нет, получатели объявления и есть разговор.
   *
   * ⚠️ **ЧИСЛО, а не поимённый список, и это решение владельца 30.08.2026:
   * «пусть будет пока так, 3 из 8».** До окна переписки под сообщением стоял
   * список «кто и когда открыл», и его отсутствие легко принять за недосмотр
   * рефакторинга. Это не недосмотр. Квитанцию просила Дина словами «кинул ссылку
   * и не знаешь, посмотрели или нет» (`docs/castdev-dina.md`, раздел 3), и на
   * этот вопрос число отвечает полностью; поимённый список отвечает на другой
   * вопрос, «кому написать лично», и его никто не заказывал, а под каждым
   * сообщением подряд он превращается в мусор. Если он однажды понадобится,
   * правильная форма это число с раскрытием по нажатию, а не список по
   * умолчанию. Слово «пока» из решения тоже держим: оно про сегодняшний день.
   */
  recipientCount: z.number().int().nonnegative().optional(),
  seenCount: z.number().int().nonnegative().optional(),
});
export type ConversationMessage = z.infer<typeof ConversationMessage>;

/**
 * Страница переписки: шапка окна и порция сообщений.
 *
 * ⚠️ **Свежие идут ПЕРВЫМИ, и это решение, а не привычка.** Окно переписки
 * открывается на свежем конце, значит первая страница обязана приезжать без
 * курсора, а «догрузить более старые» обязано быть простым продолжением того же
 * запроса. С обратным порядком (старые первыми) первая страница требовала бы
 * знать конец списка, то есть курсор появлялся бы РАНЬШЕ первой порции. Экран
 * переворачивает массив при отрисовке, и это единственная цена.
 */
export const ConversationPageResponse = z.object({
  id: ConversationId,
  kind: ConversationKind,
  title: z.string(),
  myRole: z.enum(["teacher", "student"]),
  /**
   * Можно ли написать в этот разговор прямо сейчас.
   *
   * ⚠️ Это СОСТОЯНИЕ ПАРЫ, а не право на чтение: `false` означает ровно
   * `409 student_not_linked` от `addAnnouncementReply`, то есть «ссылка
   * отозвана». Списано поле буквально с `canReply` снятой карточки объявления.
   * Прошлое такой ученик дочитывает, свои слова удаляет. У преподавателя всегда
   * `true`.
   */
  canReply: z.boolean(),
  /**
   * Куда писать: `POST /v1/announcements/:replyToId/replies`.
   *
   * ⚠️ **Называет его сервер, а не экран.** Ответ в схеме прикреплён к
   * объявлению, а не к разговору, и «самое свежее объявление» экран сам вывести
   * не может: при длинной переписке свежее объявление лежит ЗА страницей, ниже
   * последних ответов.
   */
  replyToId: AnnouncementId,
  /**
   * Кто в этой группе, именами. Поля нет вовсе у ЛИЧНОГО окна.
   *
   * ⚠️ **Состав принадлежит РАЗГОВОРУ, а не сообщению** (решение владельца
   * 30.08.2026). До окна переписки он лежал в карточке одного объявления и
   * отвечал на вопрос «кому это ушло»; в окне тот же список отвечает на вопрос
   * «кто в этой группе», и повторять его под каждым письмом незачем. Отсюда и
   * источник: состав группы СЕЙЧАС, а не получатели прошлых рассылок. Ушедший из
   * группы поэтому не строчка с заглушкой, а отсутствие строчки: его в группе
   * нет.
   *
   * ⚠️ **Форм ровно три, и различает их читатель, а не флаг в запросе:**
   *
   * | читатель | что приезжает |
   * |---|---|
   * | преподаватель, окно группы | участники именами из его списка учеников |
   * | ученик ВИДИМОЙ группы | участники внутригрупповым именем |
   * | любая роль, личное окно | поля нет |
   *
   * Четвёртой формы («ученик скрытой группы») не существует по построению:
   * окна группы у него нет вовсе, значит и шапки, в которую состав кладут.
   *
   * ⚠️ **Профильное имя ученику не уезжает НИКОГДА в форме, где преподаватель
   * его не выбирал**: имя собирает то же единственное место, что подписывает
   * автора сообщения (`authorNameFor` на сервере), и незаданное внутригрупповое
   * разрешается в профильное ровно по правилу `memberNameShown` (решение
   * владельца 29.08.2026, цену называет предупреждение перед раскрытием).
   *
   * Квитанций тут нет ни в одной форме: «кто в группе» и «кто открыл» это разные
   * вопросы, и второй остаётся числами у сообщения (`seenCount`).
   */
  members: z.array(z.string()).optional(),
  items: z.array(ConversationMessage),
  /** Курсор к более старой странице. `null` означает «дальше ничего нет». */
  nextCursor: z.string().nullable(),
});
export type ConversationPageResponse = z.infer<typeof ConversationPageResponse>;
